import React, { useCallback, useEffect, useState } from "react";
import { Badge, Card, EmptyState, Loader, Table } from "../../../../ui";
import type { BadgeTone } from "../../../../ui";
import { toast } from "react-toastify";
import { getRmFeedback, type RmFeedbackRow } from "../../../../Api/apiUtils";
import "./RmHistory.scss";

function splitWhen(iso?: string | null): { date: string; time: string } | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { date: String(iso), time: "" };
  return {
    date: d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }),
    time: d.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    }),
  };
}

function goalTone(value: boolean | string | undefined | null): "yes" | "no" | "muted" {
  if (value === true || String(value).toLowerCase() === "true") return "yes";
  if (value === false || String(value).toLowerCase() === "false") return "no";
  return "muted";
}

const GOAL_TONES: Record<"yes" | "no" | "muted", BadgeTone> = {
  yes: "success",
  no: "danger",
  muted: "neutral",
};

function goalLabel(value: boolean | string | undefined | null): string {
  const tone = goalTone(value);
  if (tone === "yes") return "Yes";
  if (tone === "no") return "No";
  const text = String(value ?? "").trim();
  return text || "—";
}

function displayValue(value: unknown): string {
  const text = String(value ?? "").trim();
  return text || "—";
}

const WhenCell: React.FC<{ iso?: string | null }> = ({ iso }) => {
  const when = splitWhen(iso);
  if (!when) return <>—</>;
  return (
    <div className="rm-history__when">
      <span className="ds-strong">{when.date}</span>
      {when.time ? <span className="rm-history__time ds-text-subtle">{when.time}</span> : null}
    </div>
  );
};

const RmHistory: React.FC<{ studentUserId?: string | number }> = ({ studentUserId }) => {
  const [rows, setRows] = useState<RmFeedbackRow[]>([]);
  const [loading, setLoading] = useState(false);
  const id = String(studentUserId ?? "").trim();

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const data = await getRmFeedback(id);
      const list: RmFeedbackRow[] = Array.isArray(data?.data) ? data.data : [];
      setRows(list);
    } catch (error: any) {
      console.error(error);
      toast.error(error?.response?.data?.message || "Failed to load renewal info.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!id) {
    return <Loader label="Still loading this learner's details…" />;
  }

  if (loading) {
    return <Loader label="Loading renewal info…" />;
  }

  if (!rows.length) {
    return (
      <EmptyState
        title="No renewal info"
        description="Renewal feedback from the RM team will appear here once it is recorded."
      />
    );
  }

  const showTeacher = rows.some((row) => String(row.teacherName ?? "").trim());
  const showCourse = rows.some((row) => String(row.courseName ?? "").trim());

  return (
    <Card
      flush
      title="Renewal Info"
      actions={
        <span className="rm-history__count ds-text-muted">
          {rows.length} {rows.length === 1 ? "record" : "records"}
        </span>
      }
    >
      <Table caption="Renewal info" className="rm-history__table">
        <thead>
          <tr>
            <th>Created</th>
            <th>Updated</th>
            <th className="ds-num">Class number</th>
            {showTeacher ? <th>Teacher</th> : null}
            {showCourse ? <th>Course</th> : null}
            <th>Goals discussed</th>
            <th className="ds-num">Lessons left</th>
            <th>Comments</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={`${row.classNumber || row.class_id || "class"}-${row.student_user_id || index}-${row.createdAt || index}`}>
              <td><WhenCell iso={row.createdAt} /></td>
              <td><WhenCell iso={row.updatedAt} /></td>
              <td className="ds-num">{displayValue(row.classNumber)}</td>
              {showTeacher ? <td>{displayValue(row.teacherName)}</td> : null}
              {showCourse ? <td>{displayValue(row.courseName)}</td> : null}
              <td>
                <Badge tone={GOAL_TONES[goalTone(row.learningGoalsDiscussed)]}>
                  {goalLabel(row.learningGoalsDiscussed)}
                </Badge>
              </td>
              <td className="ds-num">{displayValue(row.lessonsRemaining)}</td>
              <td className="rm-history__comments">{displayValue(row.comments)}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Card>
  );
};

export default RmHistory;
